import * as r from '../../react-utils'

import { saveAs } from 'file-saver'
import html2canvas from 'html2canvas'
import { PDFDocument } from 'pdf-lib'

export default async function downloadReportAsPdf(
  customerName: string,
): Promise<void> {
  const pageElements = document.querySelectorAll<HTMLElement>('[data-pdf-page]')
  if (pageElements.length === 0) return

  const now = new Date()
  const date = now
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    })
    .replace(/,?\s/g, '-')
  const time = now
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(/:/g, '-')
  const fileName = `${customerName}_${date}_${time}.pdf`

  const safari = r.isSafari()

  // Safari's html2canvas standard renderer (foreignObject disabled) has two
  // quirks that require inline style patches before capture:
  // 1) Underlines render too close to text — html2canvas doesn't support
  //    text-underline-offset, so we swap underlines for border-bottom and add
  //    padding to create spacing.
  // 2) MUI input underline pseudo-elements have an opaque background that
  //    produces a white bar clipping over the text — we force input elements
  //    to have transparent backgrounds.
  // Safari's html2canvas standard renderer (foreignObject disabled) has two
  // quirks:
  // 1) Underlines render too close to text — html2canvas doesn't support
  //    text-underline-offset, so we swap underlines for border-bottom with
  //    padding to create spacing.
  // 2) MUI inputs render a white bar that clips over the text — no CSS fix
  //    works reliably, so we swap <input> elements for <span> elements with
  //    matching styles and restore them after capture.
  const safariStylePatched: { el: HTMLElement; orig: string }[] = []
  const safariInputSwapped: {
    span: HTMLSpanElement
    input: HTMLInputElement
    parent: Node
  }[] = []
  if (safari) {
    document
      .querySelectorAll<HTMLInputElement>('[data-pdf-page] input')
      .forEach((input) => {
        const cs = getComputedStyle(input)
        const span = document.createElement('span')
        span.textContent = input.value
        span.style.cssText = `
        font: ${cs.font};
        color: ${cs.color};
        letter-spacing: ${cs.letterSpacing};
        padding: ${cs.padding};
        border: none;
        border-bottom: ${cs.borderBottom};
        display: inline-block;
        width: ${cs.width};
        box-sizing: ${cs.boxSizing};
        background: transparent;
      `
        if (input.parentNode) {
          safariInputSwapped.push({ span, input, parent: input.parentNode })
          input.parentNode.replaceChild(span, input)
        }
      })

    document
      .querySelectorAll<HTMLElement>('[data-pdf-page] *')
      .forEach((el) => {
        const cs = getComputedStyle(el)
        if (!cs.textDecorationLine.includes('underline')) return
        safariStylePatched.push({ el, orig: el.style.cssText })
        el.style.textDecoration = 'none'
        el.style.borderBottom = `1px solid ${cs.color}`
        el.style.paddingBottom = '2px'
      })
  }

  const pdfDoc = await PDFDocument.create()

  for (const el of Array.from(pageElements)) {
    const w = el.scrollWidth
    const h = el.scrollHeight
    if (w === 0 || h === 0) continue

    const canvas = await html2canvas(el, {
      backgroundColor: '#FFFFFF',
      foreignObjectRendering: !safari,
      scale: 2,
      scrollX: 0,
      scrollY: 0,
      useCORS: true,
      width: w,
      height: h,
    })

    if (canvas.width === 0 || canvas.height === 0) continue

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
    const imgBytes = Uint8Array.from(atob(dataUrl.split(',')[1]!), (c) =>
      c.charCodeAt(0),
    )

    const image = await pdfDoc.embedJpg(imgBytes)
    const { width, height } = image.scale(1)

    const pdfPageWidth = 595.28
    const scaleFactor = pdfPageWidth / width
    const pdfPageHeight = height * scaleFactor

    const page = pdfDoc.addPage([pdfPageWidth, pdfPageHeight])
    page.drawImage(image, {
      x: 0,
      y: 0,
      width: pdfPageWidth,
      height: pdfPageHeight,
    })
  }

  for (const { el, orig } of safariStylePatched) el.style.cssText = orig
  for (const { span, input, parent } of safariInputSwapped) {
    parent.replaceChild(input, span)
  }

  const pdfBytes = await pdfDoc.save()
  saveAs(
    new Blob([pdfBytes.buffer as ArrayBuffer], { type: 'application/pdf' }),
    fileName,
  )
}
